import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  summarizeMemoryReadRollup,
  type MemoryReadSummaryResponse,
} from '../services/memoryReadSummary';
import {
  formatRollupDayLabel,
  getSegmentLabels,
} from '../utils/memoryReadHealthLabels';

export interface MemoryReadHealthPanelProps {
  className?: string;
  summary: MemoryReadSummaryResponse | null;
  loading: boolean;
  error: string | null;
  /** Pre-translated line describing the effective rollup window (e.g. from monitoring page). */
  effectiveWindowDescription?: string;
}

function formatPct(value: number | null): string {
  if (value === null || Number.isNaN(value)) return '—';
  return `${value.toFixed(1)}%`;
}

export function MemoryReadHealthPanel({
  className,
  summary,
  loading,
  error,
  effectiveWindowDescription,
}: MemoryReadHealthPanelProps) {
  const { t, i18n } = useTranslation('dashboard', { keyPrefix: 'memoryReadHealth' });

  const computed = useMemo(() => {
    if (!summary?.rollup?.length) {
      return summarizeMemoryReadRollup([]);
    }
    return summarizeMemoryReadRollup(summary.rollup);
  }, [summary]);

  const hasNoData =
    !loading &&
    !error &&
    summary !== null &&
    (summary.total_reads === 0 || computed.totalFromRollup === 0);

  const dayBars = useMemo(() => {
    if (!summary?.by_day?.length) return [];
    const days = [...summary.by_day].sort((a, b) => a.date.localeCompare(b.date));
    const max = Math.max(...days.map((d) => d.total_reads), 1);
    return days.map((d) => ({
      date: d.date,
      pct: Math.max((d.total_reads / max) * 100, d.total_reads > 0 ? 8 : 0),
      total: d.total_reads,
    }));
  }, [summary]);

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-5 w-5 text-primary" aria-hidden />
            <span>{t('title')}</span>
          </CardTitle>
          {effectiveWindowDescription ? (
            <CardDescription className="text-xs">{effectiveWindowDescription}</CardDescription>
          ) : null}
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3" aria-busy="true">
            <div className="h-10 bg-muted rounded-md w-1/3" />
            <div className="h-4 bg-muted rounded w-2/3" />
            <div className="h-24 bg-muted rounded-md" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-5 w-5 text-primary" aria-hidden />
            <span>{t('title')}</span>
          </CardTitle>
          {effectiveWindowDescription ? (
            <CardDescription className="text-xs">{effectiveWindowDescription}</CardDescription>
          ) : null}
        </CardHeader>
        <CardContent>
          <div aria-live="polite">
            <Alert variant="destructive">
              <AlertTitle>{t('errorTitle')}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (hasNoData) {
    return (
      <Card className={className}>
        <CardHeader className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-5 w-5 text-primary" aria-hidden />
            <span>{t('title')}</span>
          </CardTitle>
          {effectiveWindowDescription ? (
            <CardDescription className="text-xs">{effectiveWindowDescription}</CardDescription>
          ) : null}
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t('emptyDescription')}</p>
          <p className="text-xs text-muted-foreground mt-2">{t('coverageNote')}</p>
        </CardContent>
      </Card>
    );
  }

  if (!summary) {
    return null;
  }

  const strictLabel = t('strictRateLabel');
  const relaxedLabel = t('relaxedRateLabel');

  return (
    <Card className={className}>
      <CardHeader className="space-y-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-5 w-5 text-primary" aria-hidden />
            <span>{t('title')}</span>
          </CardTitle>
          <Badge variant="secondary" className="font-normal">
            {t('sourceBadge')}
          </Badge>
        </div>
        {effectiveWindowDescription ? (
          <CardDescription className="text-xs">{effectiveWindowDescription}</CardDescription>
        ) : null}
        <p className="text-xs text-muted-foreground">{t('coverageNote')}</p>
        <p className="text-xs text-muted-foreground/90">{t('summaryHint')}</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap gap-6 items-start">
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-muted-foreground">
                {strictLabel}
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="rounded-sm text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={t('strictRateAriaHelp')}
                    >
                      <Info className="h-4 w-4" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>{t('strictRateTooltip')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-3xl font-semibold tabular-nums" aria-label={strictLabel}>
              {formatPct(computed.strictRate)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('countPair', {
                num: computed.strictSuccessCount,
                den: computed.totalFromRollup,
              })}
            </p>
          </div>

          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-muted-foreground">
                {relaxedLabel}
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="rounded-sm text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={t('relaxedRateAriaHelp')}
                    >
                      <Info className="h-4 w-4" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>{t('relaxedRateTooltip')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-xl font-semibold tabular-nums text-muted-foreground">
              {formatPct(computed.relaxedRate)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('relaxedCountPair', {
                num: computed.relaxedSuccessCount,
                den: computed.totalFromRollup,
              })}
            </p>
          </div>
        </div>

        {dayBars.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-foreground mb-2">{t('volumeByDay')}</h3>
            <div
              className="flex h-28 gap-1 border-b border-border"
              role="img"
              aria-label={t('volumeByDayAria')}
            >
              {dayBars.map((d) => (
                <TooltipProvider key={d.date}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                        <div className="flex min-h-0 flex-1 items-end justify-center pb-1">
                          <div
                            className="w-full max-w-[14px] rounded-sm bg-primary/70"
                            style={{
                              height: `${d.pct}%`,
                              minHeight: d.total > 0 ? '4px' : undefined,
                            }}
                          />
                        </div>
                        <span className="text-[10px] text-muted-foreground truncate text-center">
                          {formatRollupDayLabel(d.date, i18n.language)}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="tabular-nums">
                        {formatRollupDayLabel(d.date, i18n.language)} ({d.date}): {d.total}{' '}
                        {t('reads')}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-sm font-medium text-foreground mb-2">{t('topSegments')}</h3>
          <TooltipProvider delayDuration={300}>
            <ul className="space-y-2" role="list">
              {computed.topRollupByCount.map((r, i) => {
                const labels = getSegmentLabels(t, r);
                const aria = `${labels.operationLabel}, ${labels.integrationLabel}, ${labels.outcomeLabel}`;
                return (
                  <li
                    key={`${r.operation}-${r.integration_type}-${r.space_id}-${r.outcome}-${i}`}
                    className="flex flex-wrap items-stretch justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
                    role="listitem"
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className="flex min-w-0 flex-1 cursor-default flex-col gap-0.5 text-left"
                          aria-label={aria}
                        >
                          <span className="truncate font-medium text-foreground">
                            {labels.operationLabel}
                          </span>
                          <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                            <span className="truncate">{labels.integrationLabel}</span>
                            <span aria-hidden className="shrink-0">
                              ·
                            </span>
                            <span className="truncate">{labels.outcomeLabel}</span>
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        <p className="text-xs font-medium">{t('segmentRowTooltipTitle')}</p>
                        <p className="mt-1 font-mono text-xs tabular-nums text-muted-foreground">
                          {r.operation} · {r.integration_type} · {r.outcome}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    <Badge variant="outline" className="tabular-nums shrink-0 self-center">
                      {r.count}
                    </Badge>
                  </li>
                );
              })}
            </ul>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
}
